import {
  ChangeDetectionStrategy, Component, EventEmitter,
  Input, Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { MarkdownPipe } from '../../../../shared/pipes/markdown.pipe';
import { ToolActivityPillsComponent } from '../../../../shared/components/tool-activity-pills/tool-activity-pills.component';
import { ContactPickerComponent } from '../../../../shared/components/contact-picker/contact-picker.component';
import { BookingCalendarComponent, SlotSelectedEvent } from '../../../../shared/components/booking-calendar/booking-calendar.component';
import { GuestWizardComponent } from '../../../../shared/components/guest-wizard/guest-wizard.component';
import { ArtifactCardComponent } from '../../../../shared/components/artifact-card/artifact-card.component';

import { ExecutivePickerComponent } from '../executive-picker/executive-picker.component';
import { CallObjectiveSelectorComponent } from '../call-objective-selector/call-objective-selector.component';
import { RescheduleCardComponent } from '../reschedule-card/reschedule-card.component';
import { BookingSummaryCardComponent } from '../booking-summary-card/booking-summary-card.component';
import { ConfirmationCardComponent } from '../confirmation-card/confirmation-card.component';
import { CancelCardComponent } from '../cancel-card/cancel-card.component';
import { AddParticipantsCardComponent } from '../add-participants-card/add-participants-card.component';
import { PostBookingFollowupComponent } from '../post-booking-followup/post-booking-followup.component';
import { MeetingPickerComponent, MeetingSelectedEvent } from '../meeting-picker/meeting-picker.component';

import { ContactItem, ExecutiveItem, PCheckChatMessage, AddedGuest } from '../../models/pcheck.model';
import {
  browserTimezone, execTimezoneAbbr, formatZonedDateTimeLine, isIsoUtc,
} from '../../helpers/booking-timezone';

// ── Events emitted up to the host (agenthome-chat) ───────────────────────────

export interface PCheckAction {
  type:
    | 'send-message'
    | 'contact-selected'
    | 'executive-selected'
    | 'slot-selected'
    | 'reschedule-slot-selected'
    | 'booking-done'
    | 'booking-failed'
    | 'booking-retry-same'
    | 'booking-retry-different'
    | 'meeting-selected'
    | 'reschedule-done'
    | 'cancel-confirmed'
    | 'cancel-dismissed'
    | 'participants-confirmed'
    | 'participants-cancelled'
    | 'call-objective-selected'
    | 'guests-confirmed'
    | 'post-booking-answered';
  payload?: unknown;
  msgIdx: number;
}

@Component({
  selector: 'app-pcheck-renderer',
  standalone: true,
  imports: [
    CommonModule,
    MarkdownPipe,
    ToolActivityPillsComponent,
    ContactPickerComponent,
    BookingCalendarComponent,
    GuestWizardComponent,
    ArtifactCardComponent,
    ExecutivePickerComponent,
    CallObjectiveSelectorComponent,
    RescheduleCardComponent,
    BookingSummaryCardComponent,
    ConfirmationCardComponent,
    CancelCardComponent,
    AddParticipantsCardComponent,
    PostBookingFollowupComponent,
    MeetingPickerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './pcheck-renderer.component.html',
  styleUrls: ['./pcheck-renderer.component.scss'],
})
export class PCheckRendererComponent {
  /** The chat message to render. */
  @Input() msg!: PCheckChatMessage;
  /** Index in the messages array — passed back in every emitted action. */
  @Input() msgIdx = 0;
  /**
   * True when this message is no longer the latest agent message.
   * Interactive widgets (pickers, wizards) become read-only.
   */
  @Input() isPast = false;
  /** True while the agent is still processing the current turn. */
  @Input() isProcessing = false;
  /** agent_id for this message (e.g. 'PCHECK' or 'IMPACT_PCHECK'). Used to
   *  restrict the timezone picker for IMPACT_PCHECK to the single config zone. */
  @Input() agentId?: string | null;

  get isImpact(): boolean { return this.agentId === 'IMPACT_PCHECK_AGENT'; }

  /** All user interactions are funnelled through this single output. */
  @Output() action = new EventEmitter<PCheckAction>();

  // ── Helpers ──────────────────────────────────────────────────────────────

  /** The confirmed booking time arrives as a raw UTC ISO instant. Render it in
   *  the zone the picker used (Impact: the event zone) with a short zone label
   *  instead of leaking the ISO string; pre-formatted legacy strings pass through. */
  confirmedTimeLabel(raw: string | undefined, tzIana: string | undefined): string {
    if (!raw) return '';
    if (!isIsoUtc(raw)) return raw;
    const tz = tzIana || browserTimezone();
    return `${formatZonedDateTimeLine(raw, tz)} (${execTimezoneAbbr(tz)})`;
  }

  /** True when any choice-UI widget is shown — hides the markdown text.
   *
   *  Each condition here must mirror its `@if` guard in the template exactly.
   *  Widgets that have `!isPast` in their template guard must carry the same
   *  check here, otherwise markdown is hidden on past messages even though
   *  no interactive card is rendered.
   */
  /** True when any choice-UI widget is shown — hides the markdown text.
   *
   *  Every condition here must mirror its `@if` guard in the template exactly —
   *  including isPast checks, dismissed flags, and companion-field requirements.
   *  A mismatch hides markdown while nothing is actually rendered.
   */
  get hasChoiceUi(): boolean {
    const m    = this.msg;
    const live = !this.isPast; // widgets with !isPast guard in the template

    return !!(
      // ── Pickers: shown live OR frozen (disabled) after a selection ─────────
      // Contacts: always rendered (read-only on past), so hide the prose too —
      // mirror the contact-picker template guard exactly (suppressed during the
      // add-participants / meeting-picker / booking-slots flows).
      (m.contactsList?.length  && !m.addParticipantsDetails && !m.multipleMeetings?.length && !m.bookingSlots?.length)  ||
      (m.executivesList?.length                             && (live || !!m.executivesSelected)) ||
      (m.callObjectiveOptions?.length                       && (live || !!m.callObjectiveConfirmed)) ||
      // Multi-meeting picker — live-only (mirrors its !isPast template guard)
      (live && m.multipleMeetings?.length)                                          ||
      // Guest wizard: shown live (interactive) or past (disabled) — mirrors its template guard
      (m.additionalGuestContacts !== undefined && !m.bookingSlots?.length && !m.addParticipantsDetails) ||
      // Booking calendar: shown live, or past if booking was confirmed
      (m.bookingSlots?.length && (live || !!m.bookingConfirmed))                   ||
      // Reschedule calendar: shown live, or past if the reschedule was confirmed
      (m.rescheduleSlots?.length && (live || !!m.rescheduleSlotsConfirmed))        ||
      // Booking summary card needs BOTH bookingUrl AND bookingSummary
      (live && m.bookingUrl && m.bookingSummary)                                   ||

      // ── Confirmed-or-live widgets ─────────────────────────────────────────
      // Reschedule card: live (interactive) or confirmed in current session
      (m.rescheduleUrl && !!m.rescheduleDetails && (live || !!m.rescheduleConfirmed)) ||
      // Cancel card: live unless dismissed; stays visible while confirmed in current session
      (m.cancelDetails && !m.cancelDismissed && (live || !!m.cancelConfirmed))      ||
      // Add-participants: live unless dismissed; stays visible while confirmed (restored from _wizard)
      (m.addParticipantsDetails && !m.addParticipantsDismissed && (live || !!m.addParticipantsConfirmed)) ||
      (m.reportUrl && !m.isPulseCheckComplete)
    );
  }

  // ── Dispatch helpers ─────────────────────────────────────────────────────

  private emit(type: PCheckAction['type'], payload?: unknown): void {
    this.action.emit({ type, payload, msgIdx: this.msgIdx });
  }

  onContactSelected(contact: ContactItem): void       { this.emit('contact-selected', contact); }
  onExecutiveSelected(exec: ExecutiveItem): void       { this.emit('executive-selected', exec); }
  onSlotSelected(e: SlotSelectedEvent): void           { this.emit('slot-selected', e); }
  onRescheduleSlotSelected(e: SlotSelectedEvent): void { this.emit('reschedule-slot-selected', e); }
  onBookingDone(): void                                { this.emit('booking-done'); }
  onBookingFailed(): void                              { this.emit('booking-failed'); }
  onRetryExecutive(): void                             { this.emit('booking-retry-same'); }
  onDifferentExecutive(): void                         { this.emit('booking-retry-different'); }
  onMeetingSelected(e: MeetingSelectedEvent): void     { this.emit('meeting-selected', e); }
  onRescheduled(): void                                { this.emit('reschedule-done'); }
  onCancelConfirmed(p: { reason: string }): void       { this.emit('cancel-confirmed', p); }
  onCancelDismissed(): void                            { this.emit('cancel-dismissed'); }
  onParticipantsConfirmed(emails: string[]): void      { this.emit('participants-confirmed', emails); }
  onParticipantsCancelled(): void                      { this.emit('participants-cancelled'); }
  onCallObjective(option: string): void                { this.emit('call-objective-selected', option); }
  onGuestsConfirmed(guests: AddedGuest[]): void        { this.emit('guests-confirmed', guests); }
  onPostBookingAnswer(answer: 'yes' | 'no'): void      { this.emit('post-booking-answered', answer); }
}
